import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { api, errText, PHONE, PHONE_HREF, EMAIL } from "@/lib/api";
import { RuneMark } from "./Img";
import { PortalButton } from "./Header";

function Newsletter() {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/forms/newsletter", { email, website: hp });
      setDone(true);
      toast.success("You're on the list.");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return <p className="font-mono text-sm text-[color:var(--ice)]" data-testid="newsletter-success">Subscribed — thanks.</p>;
  return (
    <form onSubmit={submit} className="flex w-full max-w-md" data-testid="newsletter-form">
      <input type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="field flex-1" aria-label="Email address" data-testid="newsletter-email-input" />
      <button disabled={busy} className="btn-ice !px-5" data-testid="newsletter-submit-button">{busy ? "…" : "Subscribe"}</button>
    </form>
  );
}

const COLS = [
  ["Tuning", [["/ecu-remapping", "ECU Remapping"], ["/services", "Services"], ["/remappingfranchise", "Become a Dealer"], ["/tuning-files", "Tuning Files"], ["/about", "About"], ["/faq", "FAQ"]]],
  ["Tools", [["/tuning-tools/alientech", "Alientech KESS3"], ["/tuning-tools/autotuner", "Autotuner"], ["/tuning-tools/dimsport", "Dimsport"], ["/tuning-tools/flex", "FLEX"], ["/tuning-tools/cmd", "CMD Flashtec"]]],
  ["Shop", [["/shop", "All products"], ["/shop/category/tuning-file-credits", "File credits"], ["/software-downloads", "Downloads"], ["/blog", "Guides & News"]]],
  ["Info", [["/contact", "Contact"], ["/shipping-returns", "Shipping & Returns"], ["/terms-conditions", "Terms & Conditions"], ["/privacy-policy", "Privacy Policy"], ["/cookie-policy", "Cookie Policy"]]],
];

export function Footer() {
  return (
    <footer className="relative mt-24 border-t border-[color:var(--line)] bg-[#08090b]" data-testid="site-footer">
      <div className="knot-bg pointer-events-none absolute inset-0 opacity-[0.025]" />
      <div className="container-x relative py-16">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_2fr]">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <RuneMark className="h-10 w-10 text-[color:var(--ice)]" />
              <span className="font-display text-2xl font-bold uppercase tracking-[0.2em]">Jorvik Tuning</span>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-slate-400">Engine management specialists. Tuning tools, dyno-developed files and dealer support.</p>
            <ul className="space-y-2 text-sm">
              <li><a href={PHONE_HREF} className="flex items-center gap-3 text-slate-200 hover:text-[color:var(--ice)]" data-testid="footer-phone"><Phone className="h-4 w-4 text-[color:var(--bronze)]" />{PHONE}</a></li>
              <li><a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-slate-200 hover:text-[color:var(--ice)]" data-testid="footer-email"><Mail className="h-4 w-4 text-[color:var(--bronze)]" />{EMAIL}</a></li>
              <li className="flex items-center gap-3 text-slate-400"><MapPin className="h-4 w-4 text-[color:var(--bronze)]" />Skipton, North Yorkshire</li>
            </ul>
            <PortalButton testId="footer-customer-portal-button" />
          </div>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {COLS.map(([h, links]) => (
              <div key={h}>
                <p className="eyebrow-bronze mb-4">{h}</p>
                <ul className="space-y-2.5">
                  {links.map(([to, l]) => <li key={to}><Link to={to} className="text-sm text-slate-400 transition-colors hover:text-white">{l}</Link></li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="bronze-rule my-12 opacity-60" />
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-3 font-display text-xl uppercase tracking-wide">Mailing list</p>
            <p className="mb-4 text-sm text-slate-400">Stock arrivals and tool news. No spam.</p>
            <Newsletter />
          </div>
          <div className="flex items-center gap-5">
            <a href="https://www.facebook.com/jorviktuning/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-slate-400 hover:text-[color:var(--ice)]" data-testid="footer-facebook"><Facebook className="h-5 w-5" /></a>
            <a href="https://www.instagram.com/jorviktuning/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-slate-400 hover:text-[color:var(--ice)]" data-testid="footer-instagram"><Instagram className="h-5 w-5" /></a>
            <a href="https://www.youtube.com/channel/UCcasZMJTBqxW4kR5jsFTyrw" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-slate-400 hover:text-[color:var(--ice)]" data-testid="footer-youtube"><Youtube className="h-5 w-5" /></a>
          </div>
        </div>
        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">© {new Date().getFullYear()} Jorvik Tuning · ᛃ ᛟ ᚱ ᚢ ᛁ ᚲ</p>
      </div>
    </footer>
  );
}
